import React, { useState, useEffect, useRef } from "react";
import './Listening.css';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { isTestUnlocked } from "../utils/subscriptionHelper";
import EngdemyLogo from "../assets/Images/EngdemyUploaded.png";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const partTitles = {
    1.1: "Part 1: Listening to Problem Solving",
    1.2: "Part 1: Listening to Problem Solving",
    1.3: "Part 1: Listening to Problem Solving",
    2: "Part 2: Listening to Daily Life Conversations",
    3: "Part 3: Listening for Information",
    4: "Part 4: Listening to a News Item",
    5: "Part 5: Listening to a Discussion",
    6: "Part 6: Listening to Viewpoints",
};

const taskIntroData = {
    1.1: {
        partNumber: 1,
        partLabel: "Part 1: Listening to Problem Solving",
        heading: "Listening to Problem Solving",
        bullets: [
            "You will hear a conversation between two people. The conversation is about a problem one of them has.",
            "You will hear the conversation only once. It is in three sections.",
            "After each section, you will be asked two or three questions. You will hear the questions only once.",
            "Choose the best answer to each question."
        ]
    },
    2: {
        partNumber: 2,
        partLabel: "Part 2: Listening to a Daily Life Conversation",
        heading: "Listening to a Daily Life Conversation",
        bullets: [
            "You will hear a conversation. It is about 1.5 to 2 minutes long.",
            "Then 5 questions will appear.",
            "Choose the best answer to each question."
        ]
    },
    3: {
        partNumber: 3,
        partLabel: "Part 3: Listening for Information",
        heading: "Listening for Information",
        bullets: [
            "You will hear a conversation. It is about 2 to 2.5 minutes long.",
            "Then 6 questions will appear.",
            "Choose the best answer to each question."
        ]
    },
    4: {
        partNumber: 4,
        partLabel: "Part 4: Listening to a News Item",
        heading: "Listening to a News Item",
        bullets: [
            "You will hear a news item once. It is about 1.5 minutes long.",
            "Then 5 questions will appear.",
            "Choose the best way to complete each statement from the drop-down menu."
        ]
    },
    5: {
        partNumber: 5,
        partLabel: "Part 5: Listening to a Discussion",
        heading: "Listening to a Discussion",
        bullets: [
            "You will watch a 2-minute video.",
            "Then 8 questions will appear.",
            "Choose the best way to complete each statement from the drop-down menu."
        ]
    },
    6: {
        partNumber: 6,
        partLabel: "Part 6: Listening to Viewpoints",
        heading: "Listening to Viewpoints",
        bullets: [
            "You will hear a report. It is about 3 minutes long.",
            "Then 6 questions will appear.",
            "Choose the best way to complete each statement from the drop-down menu."
        ]
    }
};

const CelpipListeningAllParts = ({ testNumber: propTestNumber, onComplete, isFullTest = false }) => {
    const { testNumber: initialTestNumber } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const effectiveTestNumber = propTestNumber ? Number(propTestNumber) : (initialTestNumber ? Number(initialTestNumber) : 1);
    const [testNumber, setTestNumber] = useState(effectiveTestNumber);

    const reduxUser = useSelector((state) => state.auth?.authData);
    const getStoredUser = () => {
        try {
            if (reduxUser?.result) return reduxUser.result;
            if (reduxUser?.email || reduxUser?._id) return reduxUser;
            const raw = localStorage.getItem("user_info");
            if (raw) {
                const localUser = JSON.parse(raw);
                if (localUser?.result) return localUser.result;
                if (localUser?.email || localUser?._id) return localUser;
            }
            const authRaw = localStorage.getItem("authData");
            if (authRaw) {
                const localAuth = JSON.parse(authRaw);
                if (localAuth?.result) return localAuth.result;
                if (localAuth?.email || localAuth?._id) return localAuth;
            }
        } catch (e) {}
        return null;
    };

    
    
    const [currentUser, setCurrentUser] = useState(getStoredUser());
    const [taskData, setTaskData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [part, setPart] = useState(1.1);
    const [step, setStep] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [score, setScore] = useState(null);
    const [showInstructions, setShowInstructions] = useState(!location.state?.showResults);
    const [showTaskIntro, setShowTaskIntro] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [showTaskAudio, setShowTaskAudio] = useState(false);
        const [questionAudioPlaying, setQuestionAudioPlaying] = useState(false);
    const [timer, setTimer] = useState(30);
    const [timerActive, setTimerActive] = useState(false);
    const taskAudioRef = useRef(null);
    const questionAudioRef = useRef(null);
    const timerRef = useRef(null);
    const audioEndTimerRef = useRef(null);
    const [audioEndCountdown, setAudioEndCountdown] = useState(null);
    const userId = reduxUser?.result?._id || JSON.parse(localStorage.getItem("user_info") || "{}")?.result?._id;
                
    const stopSpeech = React.useCallback(() => {
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        }
    }, []);

    const playSpeech = React.useCallback((text) => {
        if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.pitch = 1.0;
        utterance.lang = "en-US";

        try {
            const voices = window.speechSynthesis.getVoices();
            const enVoice = voices.find(v => v.lang.startsWith("en-") && (v.name.includes("Natural") || v.name.includes("Google") || v.name.includes("Samantha") || v.name.includes("David") || v.name.includes("Zira")));
            if (enVoice) utterance.voice = enVoice;
        } catch (e) {}

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        window.speechSynthesis.speak(utterance);
    }, []);

    const toggleSpeech = () => {
        if (isSpeaking) {
            stopSpeech();
        } else {
            const intro = taskIntroData[part];
            if (intro) {
                playSpeech(`${intro.heading}. ` + intro.bullets.join(" "));
            }
        }
    };

    useEffect(() => {
        if (showTaskIntro && taskIntroData[part]) {
            const intro = taskIntroData[part];
            const speechText = `${intro.heading}. ` + intro.bullets.join(" ");
            playSpeech(speechText);
        } else {
            stopSpeech();
        }
        return () => stopSpeech();
    }, [showTaskIntro, part, playSpeech, stopSpeech]);

    useEffect(() => {
        if (propTestNumber) {
            setTestNumber(Number(propTestNumber));
        }
    }, [propTestNumber]);

    useEffect(() => {
        const storedUser = getStoredUser();
        const user = storedUser || {
            _id: "guest-user",
            email: "",
            username: "Guest"
        };
        setCurrentUser(user);

        if (Number(testNumber) > 2) {
            if (!storedUser) {
                alert("Please login to access this test.");
                navigate("/login");
                return;
            }

            if (!isTestUnlocked(Number(testNumber))) {
                alert("This test is locked. Please upgrade your account to access it.");
                navigate("/pricing");
                return;
            }
        }

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const res = await fetch(`https://engdemyeducation.com/api/listening/${testNumber}`);
                if (!res.ok) throw new Error("Failed to fetch test data");

                const data = await res.json();
                setTaskData(data);

                const isShowingResults = location.state?.showResults;

                if (!isShowingResults) {
                    setShowInstructions(true);
                    setPart(1.1);
                    setStep(0);
                    setShowTaskAudio(true);
                    setSubmitted(false);
                    setScore(null);
                    setQuestionAudioPlaying(false);
                    setTimerActive(false);
                    setTimer(30);
                    clearInterval(timerRef.current);

                    const answers = {};
                    ["Task1.1", "Task1.2", "Task1.3", "Task2", "Task3", "Task4", "Task5", "Task6"].forEach((taskKey) => {
                        if (data[taskKey]) {
                            data[taskKey].Questions.forEach((_, i) => {
                                answers[`${taskKey}-q${i}`] = null;
                            });
                        }
                    });
                    setSelectedAnswers(answers);
                }

            } catch (err) {
                setError(err.message);
                setTaskData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [testNumber, navigate, location.state]);

    // Handle showResults from CheckProgress
    useEffect(() => {
        if (location.state?.showResults && location.state?.savedAttempt && taskData) {
            const saved = location.state.savedAttempt;
            const answers = saved.answers || [];

            let correct = 0;
            const answerKey = {};
            const allTaskKeys = ["Task1.1", "Task1.2", "Task1.3", "Task2", "Task3", "Task4", "Task5", "Task6"];
            
            let answerIndex = 0;
            
            allTaskKeys.forEach(taskKey => {
                const taskQuestions = taskData[taskKey]?.Questions || [];
                taskQuestions.forEach((q, qIndex) => {
                    const key = `${taskKey}-q${qIndex}`;
                    const savedAns = answers[answerIndex];
                    
                    const selectedValue = savedAns?.selectedOption || "NO ANSWER"; 
                    const isCorrect = savedAns?.isCorrect || false;
                    
                    answerKey[key] = {
                        question: q.Question,
                        correctAnswer: q.CorrectAnswer,
                        selectedAnswer: selectedValue,
                        isCorrect: isCorrect,
                        taskKey: taskKey,
                        questionIndex: qIndex
                    };
                    
                    if (isCorrect) correct++;
                    answerIndex++;
                });
            });

            setScore({
                correct: saved.attemptScore || correct,
                total: 38,
                clbLevel: saved.clbScore,
                answerKey: answerKey
            });
            setSubmitted(true);
            setShowInstructions(false);

            window.history.replaceState({}, document.title);
        }
    }, [taskData, location.state]);

    const getCurrentTaskKey = () => `Task${part}`;

    const getTimerDuration = React.useCallback(() => {
        if ([1.1, 1.2, 1.3, 2, 3].includes(part)) {
            return 30;
        } else if ([4, 5, 6].includes(part) && taskData) {
            const currentTask = taskData[getCurrentTaskKey()];
            return currentTask?.tasktime || 120;
        }
        return 30;
    }, [part, taskData]);

    useEffect(() => {
        setQuestionAudioPlaying(false);
        setTimerActive(false);
        setAudioEndCountdown(null);
        setTimer(getTimerDuration());
        clearInterval(timerRef.current);
        clearInterval(audioEndTimerRef.current);
    }, [part, getTimerDuration]);

    useEffect(() => {
        if (showTaskAudio && taskAudioRef.current) {
            taskAudioRef.current.currentTime = 0;
            const playPromise = taskAudioRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(() => {});
            }
        }
    }, [showTaskAudio, part]);

    const handleNext = React.useCallback(() => {
        clearInterval(timerRef.current);
        setTimerActive(false);

        const currentTask = taskData ? taskData[getCurrentTaskKey()] : null;
        const currentQuestions = currentTask?.Questions || [];

        if (step < currentQuestions.length - 1) {
            setStep(prev => prev + 1);
        } else {
            let nextPart;
            if (part === 1.1) nextPart = 1.2;
            else if (part === 1.2) nextPart = 1.3;
            else if (part === 1.3) nextPart = 2;
            else if (part === 2) nextPart = 3;
            else if (part === 3) nextPart = 4;
            else if (part === 4) nextPart = 5;
            else if (part === 5) nextPart = 6;
            else nextPart = null;

            if (nextPart) {
                setPart(nextPart);
                setStep(0);
                if ([2, 3, 4, 5, 6].includes(nextPart)) {
                    setShowTaskIntro(true);
                    setShowTaskAudio(false);
                } else {
                    setShowTaskIntro(false);
                    setShowTaskAudio(true);
                }
            } else {
                setStep("submit");
            }
        }
    }, [step, part, taskData]);

    const startTimer = React.useCallback(() => {
        const duration = getTimerDuration();
        setTimer(duration);
        setTimerActive(true);
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    setTimerActive(false);
                    if ([1.1, 1.2, 1.3, 2, 3].includes(part)) {
                        handleNext();
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [getTimerDuration, part, handleNext]);

    useEffect(() => {
        if (!showInstructions && !showTaskIntro && !showTaskAudio) {
            if ([1.1, 1.2, 1.3, 2, 3].includes(part)) {
                if (!questionAudioPlaying && !timerActive) {
                    startTimer();
                }
            } else if ([4, 5, 6].includes(part)) {
                if (!timerActive) {
                    startTimer();
                }
            }
        }
    }, [showInstructions, showTaskIntro, showTaskAudio, part, questionAudioPlaying, timerActive, startTimer]);

    const handleTaskAudioEnd = () => {
        setAudioEndCountdown(6);
        clearInterval(audioEndTimerRef.current);

        let remaining = 6;
        audioEndTimerRef.current = setInterval(() => {
            remaining -= 1;
            if (remaining <= 0) {
                clearInterval(audioEndTimerRef.current);
                setAudioEndCountdown(null);
                setShowTaskAudio(false);
                setStep(0);
            } else {
                setAudioEndCountdown(remaining);
            }
        }, 1000);
    };

    const handleQuestionAudioEnd = () => {
        setQuestionAudioPlaying(false);
        startTimer();
    };

    
    const getCurrentQuestions = () => {
        if (!taskData) return [];
        const currentTask = taskData[getCurrentTaskKey()];
        return currentTask?.Questions || [];
    };

    const questions = getCurrentQuestions();

    const handleAnswerChange = (qIndex, value) => {
        const key = `${getCurrentTaskKey()}-q${qIndex}`;
        setSelectedAnswers((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleDropdownChange = (qIndex, value) => {
        const key = `${getCurrentTaskKey()}-q${qIndex}`;
        setSelectedAnswers((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const getFeedbackForScore = (correct) => {
        if (correct >= 33) return { clb: "CLB 10–12", stars: 5, feedback: "Excellent! 🌟🌟🌟🌟🌟" };
        if (correct >= 31) return { clb: "CLB 9", stars: 4, feedback: "Very Good! 🌟🌟🌟🌟" };
        if (correct >= 28) return { clb: "CLB 8", stars: 3, feedback: "😊 Keep up the good work" };
        if (correct >= 24) return { clb: "CLB 7", stars: 3, feedback: "😊 You're doing well" };
        if (correct >= 19) return { clb: "CLB 6", stars: 2, feedback: "😊 Stay consistent" };
        if (correct >= 15) return { clb: "CLB 5", stars: 1, feedback: "You can do it. Keep practicing!" };
        if (correct >= 10) return { clb: "CLB 4", stars: 1, feedback: "You can do it. Keep practicing!" };
        if (correct >= 8) return { clb: "CLB 3", stars: 1, feedback: "You can do it. Keep practicing!" };
        return { clb: "Below CLB 3", stars: 0, feedback: "Don't give up. Practice more!" };
    };

    const calculateCLBListening = (score) => {
        if (score >= 33) return 10;
        if (score >= 31) return 9;
        if (score >= 28) return 8;
        if (score >= 24) return 7;
        if (score >= 19) return 6;
        if (score >= 15) return 5;
        if (score >= 10) return 4;
        if (score >= 8) return 3;
        return 2;
    };

    const handleSubmit = async () => {
        if (!taskData) return;

        let correct = 0;
        const answerKey = {};
        const allTaskKeys = ["Task1.1", "Task1.2", "Task1.3", "Task2", "Task3", "Task4", "Task5", "Task6"];
        const payloadAnswers = [];

        allTaskKeys.forEach((taskKey) => {
            const taskQuestions = taskData[taskKey]?.Questions;
            if (taskQuestions) {
                taskQuestions.forEach((question, qIndex) => {
                    const key = `${taskKey}-q${qIndex}`;
                    const selectedValue = selectedAnswers[key];
                    const correctAnswer = question.CorrectAnswer;

                    answerKey[key] = {
                        question: question.Question,
                        correctAnswer,
                        selectedAnswer: selectedValue || "NO ANSWER",
                        isCorrect: selectedValue === correctAnswer,
                        taskKey,
                        questionIndex: qIndex,
                    };

                    if (selectedValue === correctAnswer) correct++;

                    if (selectedValue) {
                        payloadAnswers.push({
                            questionId: `Q${qIndex + 1}`,
                            selectedOption: selectedValue,
                            correctAnswer,
                            isCorrect: selectedValue === correctAnswer,
                        });
                    }
                });
            }
        });

        const clb = calculateCLBListening(correct);

        try {
            if (userId && userId !== "guest-user") {
                await fetch(`${API_BASE_URL}/api/listening-attempts`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        userId, testNumber, attemptScore: correct,
                        clbScore: clb,
                        answers: payloadAnswers,
                    }),
                });
            }
        } catch (err) {
            console.error("Error submitting listening attempt:", err);
        }

        setScore({ correct, total: 38, clbLevel: clb, answerKey });
        setSubmitted(true);

        if (onComplete) {
            onComplete(correct, []);
        }
    };

    const getTaskAudioUrl = (testNumber, part) => `https://engdemyeducation.com/image/T${testNumber}T${part}S1.mp3`;
    const getQuestionAudioUrl = (testNumber, part, index) => `https://engdemyeducation.com/image/T${testNumber}T${part}Q${index + 1}.mp3`;
    const getTaskVideoUrl = (testNumber, part) => part === 5 ? `https://engdemyeducation.com/image/T${testNumber}T5S1.mp4` : null;

    // Global NEXT button handler in top subbar
    const handleGlobalNext = () => {
        clearInterval(timerRef.current);
        clearInterval(audioEndTimerRef.current);
        setTimerActive(false);
        setAudioEndCountdown(null);
        stopSpeech();

        if (taskAudioRef.current) {
            try { taskAudioRef.current.pause(); } catch (e) {}
        }
        if (questionAudioRef.current) {
            try { questionAudioRef.current.pause(); } catch (e) {}
        }

        // If on initial instructions screen -> go to first task intro (Part 1)
        if (showInstructions) {
            setShowInstructions(false);
            setPart(1.1);
            setStep(0);
            setShowTaskIntro(true);
            setShowTaskAudio(false);
            return;
        }

        // If on task intro screen -> proceed to task audio
        if (showTaskIntro) {
            setShowTaskIntro(false);
            setShowTaskAudio(true);
            setStep(0);
            return;
        }

        // If on task audio page -> proceed to questions
        if (showTaskAudio) {
            setShowTaskAudio(false);
            setStep(0);
            return;
        }

        // If on questions for parts 1.1 through 3
        if ([1.1, 1.2, 1.3, 2, 3].includes(part)) {
            if (step < questions.length - 1) {
                setStep(prev => prev + 1);
            } else {
                let nextPart;
                if (part === 1.1) nextPart = 1.2;
                else if (part === 1.2) nextPart = 1.3;
                else if (part === 1.3) nextPart = 2;
                else if (part === 2) nextPart = 3;
                else if (part === 3) nextPart = 4;
                else nextPart = null;

                if (nextPart) {
                    setPart(nextPart);
                    setStep(0);
                    if ([2, 3, 4].includes(nextPart)) {
                        setShowTaskIntro(true);
                        setShowTaskAudio(false);
                    } else {
                        setShowTaskIntro(false);
                        setShowTaskAudio(true);
                    }
                } else {
                    handleSubmit();
                }
            }
        } else if ([4, 5, 6].includes(part)) {
            // For parts 4, 5, 6 (dropdown questions)
            let nextPart;
            if (part === 4) nextPart = 5;
            else if (part === 5) nextPart = 6;
            else nextPart = null;

            if (nextPart) {
                setPart(nextPart);
                setStep(0);
                setShowTaskIntro(true);
                setShowTaskAudio(false);
            } else {
                handleSubmit();
            }
        }
    };

    // Global BACK button handler in bottom bar
    const handleGlobalBack = () => {
        clearInterval(timerRef.current);
        clearInterval(audioEndTimerRef.current);
        setTimerActive(false);
        setAudioEndCountdown(null);
        stopSpeech();

        if (taskAudioRef.current) {
            try { taskAudioRef.current.pause(); } catch (e) {}
        }
        if (questionAudioRef.current) {
            try { questionAudioRef.current.pause(); } catch (e) {}
        }

        if (showInstructions) {
            navigate('/dashboard');
            return;
        }

        if (showTaskIntro) {
            if (part === 1.1) {
                setShowInstructions(true);
                setShowTaskIntro(false);
            } else if (part === 2) {
                setPart(1.3);
                setShowTaskIntro(false);
                setShowTaskAudio(false);
                const prevQuestions = taskData?.["Task1.3"]?.Questions || [];
                setStep(Math.max(0, prevQuestions.length - 1));
            } else if (part === 3) {
                setPart(2);
                setShowTaskIntro(false);
                setShowTaskAudio(false);
                const prevQuestions = taskData?.["Task2"]?.Questions || [];
                setStep(Math.max(0, prevQuestions.length - 1));
            } else if (part === 4) {
                setPart(3);
                setShowTaskIntro(false);
                setShowTaskAudio(false);
                const prevQuestions = taskData?.["Task3"]?.Questions || [];
                setStep(Math.max(0, prevQuestions.length - 1));
            } else if (part === 5) {
                setPart(4);
                setShowTaskIntro(false);
                setShowTaskAudio(false);
                setStep(0);
            } else if (part === 6) {
                setPart(5);
                setShowTaskIntro(false);
                setShowTaskAudio(false);
                setStep(0);
            }
            return;
        }

        if (showTaskAudio) {
            if (taskIntroData[part]) {
                setShowTaskIntro(true);
                setShowTaskAudio(false);
            } else if (part === 1.2) {
                setPart(1.1);
                const prevQuestions = taskData?.["Task1.1"]?.Questions || [];
                setStep(Math.max(0, prevQuestions.length - 1));
                setShowTaskAudio(false);
            } else if (part === 1.3) {
                setPart(1.2);
                const prevQuestions = taskData?.["Task1.2"]?.Questions || [];
                setStep(Math.max(0, prevQuestions.length - 1));
                setShowTaskAudio(false);
            }
            return;
        }

        if ([1.1, 1.2, 1.3, 2, 3].includes(part)) {
            if (step > 0) {
                setStep(prev => prev - 1);
            } else {
                setShowTaskAudio(true);
            }
        } else if ([4, 5, 6].includes(part)) {
            setShowTaskAudio(true);
        }
    };

    // Render Paragon Official Exam Top Header Bar
    const renderExamHeader = () => (
        <div className="paragon-exam-header">
            <div className="paragon-exam-header-left">
                <img src={EngdemyLogo} alt="CELPIP" className="paragon-exam-logo" />
                <span className="paragon-exam-brand">CELPIP</span>
                <span className="paragon-exam-sep">|</span>
                <span className="paragon-exam-study-link" onClick={() => navigate('/dashboard')}>
                    Study Materials
                </span>
            </div>
            <div className="paragon-exam-header-center">
                <span style={{ marginRight: 6 }}>Selected Product:</span>
                <select
                    value={testNumber}
                    onChange={(e) => {
                        const newTestNum = Number(e.target.value);
                        setTestNumber(newTestNum);
                        if (isFullTest || location.pathname.startsWith('/fulltest')) {
                            navigate(`/fulltest/${newTestNum}`);
                        } else {
                            navigate(`/listening/${newTestNum}`);
                        }
                    }}
                    className="paragon-header-select"
                >
                    {[...Array(50)].map((_, idx) => {
                        const num = idx + 1;
                        const label = num <= 2
                            ? `Free CELPIP-General Practice Test ${num}`
                            : `CELPIP-General Practice Test ${num}`;
                        return (
                            <option key={num} value={num}>
                                {label}
                            </option>
                        );
                    })}
                </select>
            </div>
            <div className="paragon-exam-header-right">
                <span className="paragon-exam-gear">⚙</span>
                <span className="paragon-exam-user-email">
                    {(currentUser?.email || currentUser?.name || "MY ACCOUNT").toUpperCase()} ▾
                </span>
            </div>
        </div>
    );

    // Render Paragon Official Sub-header Bar with NEXT Button
    const renderExamSubBar = () => {
        let subbarTitle = `Practice Test ${testNumber} - Listening Test`;
        if (showTaskIntro && taskIntroData[part]) {
            subbarTitle = `Practice Test ${testNumber} - Listening ${taskIntroData[part].partLabel}`;
        } else if (!showInstructions && partTitles[part]) {
            subbarTitle = `Practice Test ${testNumber} - Listening ${partTitles[part]}`;
        }

        return (
            <div className="paragon-exam-subbar">
                <div className="paragon-exam-subbar-title">
                    {subbarTitle}
                </div>
                {!submitted && (
                    <button className="paragon-exam-next-btn" onClick={handleGlobalNext}>
                        NEXT
                    </button>
                )}
            </div>
        );
    };

    // Render Paragon Official Bottom Bar with BACK Button
    const renderExamBottomBar = () => (
        <div className="paragon-exam-bottombar">
            <button className="paragon-exam-back-btn" onClick={handleGlobalBack}>
                BACK
            </button>
        </div>
    );

    // Render Official Footer
    const renderExamFooter = () => (
        <div className="paragon-exam-footer">
            Copyright 2026 - Engdemy Education Inc
        </div>
    );

    // Render Official Listening Instructions Screen
    const renderInstructionsPage = () => (
        <div className="paragon-instructions-wrapper">
            <div className="paragon-instructions-card">
                <div className="paragon-instructions-header">
                    <span className="paragon-info-badge">i</span>
                    <h2 className="paragon-instructions-title" style={{ fontSize: 22, fontWeight: 800, color: '#000000' }}>Listening Test Instructions</h2>
                </div>
                <ul className="paragon-instructions-list">
                    <li className="paragon-instruction-item">
                        <span className="paragon-instruction-bullet" style={{ backgroundColor: '#000000', width: 8, height: 8, marginTop: 9 }}></span>
                        <span className="paragon-instruction-text" style={{ fontSize: 17.5, color: '#000000', lineHeight: 1.6 }}>
                            On the official test, once you leave a page, you cannot go back to it to change your answers. However, in this sample test, you can.
                        </span>
                    </li>

                    <li className="paragon-instruction-item">
                        <span className="paragon-instruction-bullet" style={{ backgroundColor: '#000000', width: 8, height: 8, marginTop: 9 }}></span>
                        <span className="paragon-instruction-text" style={{ fontSize: 17.5, color: '#000000', lineHeight: 1.6 }}>
                            Please note that the order of question types on the official test may differ from the order presented here.
                        </span>
                    </li>
                    <li className="paragon-instruction-item">
                        <span className="paragon-instruction-bullet" style={{ backgroundColor: '#000000', width: 8, height: 8, marginTop: 9 }}></span>
                        <span className="paragon-instruction-text" style={{ fontSize: 17.5, color: '#000000', lineHeight: 1.6 }}>
                            This Listening Test is identical in format to the official test except that the Listening section of the official test may be slightly longer as it might contain additional questions included for research and development purposes.
                        </span>
                    </li>
                </ul>
            </div>
        </div>
    );

    // Render Task Intro Page Between Tasks (matches official Paragon screenshot)
    const renderTaskIntroPage = () => {
        const intro = taskIntroData[part];
        if (!intro) return null;

        return (
            <div className="paragon-instructions-wrapper">
                <div className="paragon-instructions-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                        <h2 style={{
                            margin: 0,
                            fontSize: 24,
                            fontWeight: 800,
                            color: '#000000',
                            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}>
                            {intro.heading}
                        </h2>
                        <button
                            onClick={toggleSpeech}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                backgroundColor: isSpeaking ? '#005a9c' : '#ffffff',
                                color: isSpeaking ? '#ffffff' : '#000000',
                                border: '1.5px solid #000000',
                                borderRadius: 4,
                                padding: '7px 16px',
                                fontSize: 14,
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                            }}
                            title="Read instructions aloud"
                        >
                            <span>{isSpeaking ? '🔊 Reading Aloud...' : '▶ Listen Instructions'}</span>
                        </button>
                    </div>

                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {intro.bullets.map((bullet, idx) => (
                            <li key={idx} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 18,
                                marginBottom: 28
                            }}>
                                <span style={{
                                    width: 9,
                                    height: 9,
                                    backgroundColor: '#000000',
                                    borderRadius: '50%',
                                    marginTop: 9,
                                    flexShrink: 0
                                }}></span>
                                <span style={{
                                    fontSize: 18.5,
                                    fontWeight: 700,
                                    color: '#000000',
                                    lineHeight: 1.6
                                }}>
                                    {bullet}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        );
    };

    // Render Active Dual Pane Content (Left Pane White, Right Pane Light Cyan)
    const renderActiveTestContent = () => {
        const partTitles = {
            1.1: "Part 1: Listening to Problem Solving",
            1.2: "Part 1: Listening to Problem Solving",
            1.3: "Part 1: Listening to Problem Solving",
            2: "Part 2: Listening to Daily Life Conversations",
            3: "Part 3: Listening for Information",
            4: "Part 4: Listening to a News Item",
            5: "Part 5: Listening to a Discussion",
            6: "Part 6: Listening to Viewpoints",
        };

        const taskInfo = {
            1.1: {
                title: "Part 1: Listening to Problem Solving",
                heading: "Listening to Problem Solving",
                instructions: [
                    "You will hear a conversation between two people. The conversation is about a problem one of them has. You will hear the conversation only once. It is in three sections.",
                    "After each section, you will be asked two or three questions. You will hear the questions only once.",
                    "Choose the best answer to each question.",
                    "After the recording is finished, click NEXT to view and answer the questions."
                ]
            },
            1.2: {
                title: "Part 1: Listening to Problem Solving",
                heading: "Listening to Problem Solving",
                instructions: [
                    "You will hear the second section of the conversation.",
                    "After the recording is finished, click NEXT to view and answer the questions."
                ]
            },
            1.3: {
                title: "Part 1: Listening to Problem Solving",
                heading: "Listening to Problem Solving",
                instructions: [
                    "You will hear the third section of the conversation.",
                    "After the recording is finished, click NEXT to view and answer the questions."
                ]
            },
            2: {
                title: "Part 2: Listening to Daily Life Conversations",
                heading: "Listening to a Daily Life Conversation",
                instructions: [
                    "You will hear a conversation. It is about 1.5 to 2 minutes long.",
                    "Then 5 questions will appear.",
                    "Choose the best answer to each question.",
                    "After the recording is finished, click NEXT to view and answer the questions."
                ]
            },
            3: {
                title: "Part 3: Listening for Information",
                heading: "Listening for Information",
                instructions: [
                    "You will hear a conversation. It is about 2 to 2.5 minutes long.",
                    "Then 6 questions will appear.",
                    "Choose the best answer to each question.",
                    "After the recording is finished, click NEXT to view and answer the questions."
                ]
            },
            4: {
                title: "Part 4: Listening to a News Item",
                heading: "Listening to a News Item",
                instructions: [
                    "You will hear a news item once. It is about 1.5 minutes long.",
                    "Then 5 questions will appear.",
                    "Choose the best way to complete each statement from the drop-down menu.",
                    "After the recording is finished, click NEXT to view and answer the questions."
                ]
            },
            5: {
                title: "Part 5: Listening to a Discussion",
                heading: "Listening to a Discussion",
                instructions: [
                    "You will watch a 2-minute video.",
                    "Then 8 questions will appear.",
                    "Choose the best way to complete each statement from the drop-down menu.",
                    "After the video is finished, click NEXT to view and answer the questions."
                ]
            },
            6: {
                title: "Part 6: Listening to Viewpoints",
                heading: "Listening to Viewpoints",
                instructions: [
                    "You will hear a report. It is about 3 minutes long.",
                    "Then 6 questions will appear.",
                    "Choose the best way to complete each statement from the drop-down menu.",
                    "After the recording is finished, click NEXT to view and answer the questions."
                ]
            }
        };

        const getDisplayQuestionNumber = (currentPart, currentStep) => {
            if (currentPart === 1.1) return currentStep + 1;
            if (currentPart === 1.2) return currentStep + 3;
            if (currentPart === 1.3) return currentStep + 6;
            return currentStep + 1;
        };

        // State 1: Showing Task Audio Stimulus ("THEN THIS")
        if (showTaskAudio) {
            const taskAudioUrl = getTaskAudioUrl(testNumber, part);
            const taskVideoUrl = getTaskVideoUrl(testNumber, part);
            const info = taskInfo[part] || {
                title: `Part ${part}`,
                heading: "Instructions",
                instructions: [
                    "You will hear a conversation or presentation. You will hear each section of the listening test only once.",
                    "After the recording is finished, click NEXT to view and answer the questions."
                ]
            };

            return (
                <div className="paragon-split-layout">
                    <div className="paragon-pane-left">
                        <h2 className="paragon-pane-part-title">{info.title}</h2>
                        <div className="paragon-audio-box">
                            {part === 5 && taskVideoUrl ? (
                                <video key={taskVideoUrl} ref={taskAudioRef} controls autoPlay preload="auto" onEnded={handleTaskAudioEnd} style={{ width: "100%", borderRadius: 4 }}>
                                    <source src={taskVideoUrl} type="video/mp4" />
                                </video>
                            ) : taskAudioUrl ? (
                                <audio key={taskAudioUrl} ref={taskAudioRef} controls autoPlay onEnded={handleTaskAudioEnd} style={{ width: "100%" }} controlsList="nodownload">
                                    <source src={taskAudioUrl} type="audio/mpeg" />
                                </audio>
                            ) : <div>No audio available for this part.</div>}
                        </div>
                        <p style={{ fontSize: 13.5, color: '#64748b', marginTop: 12 }}>
                            🔊 Listen to the audio recording carefully. Once finished, click <strong>NEXT</strong> in the top right bar to proceed to the questions.
                        </p>
                    </div>

                    <div className="paragon-pane-right">
                        <h3 className="paragon-pane-instructions-heading">{info.heading}</h3>
                        {info.instructions.map((line, idx) => (
                            <p key={idx} className="paragon-pane-instructions-text" style={{ marginTop: idx > 0 ? 12 : 0 }}>
                                {line.includes("NEXT") ? (
                                    <>
                                        {line.split("NEXT")[0]}
                                        <strong>NEXT</strong>
                                        {line.split("NEXT")[1]}
                                    </>
                                ) : line}
                            </p>
                        ))}
                        {audioEndCountdown !== null && (
                            <div className="paragon-timer-badge" style={{ marginTop: 20, backgroundColor: '#005a9c', padding: '8px 14px', borderRadius: 4 }}>
                                ⏱ Moving to next section in {audioEndCountdown}s (or click NEXT)...
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // State 2: Showing Questions for Parts 1.1 through 3 (Question-by-Question)
        if ([1.1, 1.2, 1.3, 2, 3].includes(part)) {
            const q = questions[step];
            if (!q) return <div style={{ padding: 30 }}>No question available.</div>;

            const questionAudioUrl = getQuestionAudioUrl(testNumber, part, step);
            const answerKey = `${getCurrentTaskKey()}-q${step}`;
            const displayQNum = getDisplayQuestionNumber(part, step);

            return (
                <div className="paragon-split-layout">
                    <div className="paragon-pane-left">
                        <h3 className="paragon-pane-part-title">
                            {partTitles[part]} — Question {displayQNum}
                        </h3>
                        <div className="paragon-audio-box">
                            {questionAudioUrl ? (
                                <audio key={questionAudioUrl} ref={questionAudioRef} controls autoPlay onEnded={handleQuestionAudioEnd} onPlay={() => setQuestionAudioPlaying(true)} style={{ width: "100%" }} controlsList="nodownload">
                                    <source src={questionAudioUrl} type="audio/mpeg" />
                                </audio>
                            ) : <div>No audio for this question.</div>}
                        </div>
                        {timerActive && (
                            <div className="paragon-timer-badge">
                                ⏱ Time Remaining: {timer}s
                            </div>
                        )}
                    </div>

                    <div className="paragon-pane-right">
                        <h4 className="paragon-q-title">
                            Question {displayQNum}: {q.Question}
                        </h4>
                        <div style={{ marginTop: 16 }}>
                            {["A", "B", "C", "D"].map(opt => (
                                <label key={opt} className="paragon-radio-option">
                                    <input
                                        type="radio"
                                        name={`question-${part}-${step}`}
                                        value={q[`Option${opt}`]}
                                        checked={selectedAnswers[answerKey] === q[`Option${opt}`]}
                                        onChange={() => handleAnswerChange(step, q[`Option${opt}`])}
                                    />
                                    <span>{q[`Option${opt}`]}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        // State 3: Showing Questions for Parts 4, 5, 6 (Dropdown Format)
        if ([4, 5, 6].includes(part)) {
            const currentTaskKey = getCurrentTaskKey();
            return (
                <div className="paragon-split-layout">
                    <div className="paragon-pane-left">
                        <h2 className="paragon-pane-part-title">{partTitles[part]}</h2>
                        <p className="paragon-pane-instructions-text">
                            Read the passage and select the best answer for each question from the dropdown menus on the right.
                        </p>
                        <div className="paragon-timer-badge" style={{ marginTop: 20 }}>
                            ⏱ Time Remaining: {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, "0")}
                        </div>
                    </div>

                    <div className="paragon-pane-right" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                        <h3 className="paragon-pane-instructions-heading">Questions</h3>
                        {questions.map((q, i) => {
                            const currentKey = `${currentTaskKey}-q${i}`;
                            return (
                                <div key={i} style={{ marginBottom: 22, paddingBottom: 16, borderBottom: '1px solid #d4e2ee' }}>
                                    <p style={{ fontWeight: 600, color: '#1a4971', fontSize: 14.5, marginBottom: 8 }}>
                                        Question {i + 1}: {q.Question}
                                    </p>
                                    <select
                                        value={selectedAnswers[currentKey] || ""}
                                        onChange={(e) => handleDropdownChange(i, e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '9px 12px',
                                            borderRadius: 4,
                                            border: '1px solid #c9d8e5',
                                            fontSize: 14,
                                            backgroundColor: '#ffffff',
                                            color: '#2b394a'
                                        }}
                                    >
                                        <option value="">Select an answer...</option>
                                        {["A", "B", "C", "D"].map(opt => (
                                            <option key={opt} value={q[`Option${opt}`]}>
                                                {q[`Option${opt}`]}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        return <div style={{ padding: 30 }}>Unsupported part.</div>;
    };

    // Render Completion Celebration Screen
    const renderCompletion = () => {
        if (!score) return null;
        const feedback = getFeedbackForScore(score.correct);
        const percentage = Math.round((score.correct / score.total) * 100);

        const groupedByTask = {};
        Object.entries(score.answerKey).forEach(([key, answer]) => {
            let groupKey = answer.taskKey;
            if (groupKey.startsWith("Task1.")) groupKey = "Task1";
            if (!groupedByTask[groupKey]) groupedByTask[groupKey] = [];
            groupedByTask[groupKey].push({ key, ...answer });
        });

        const taskKeysRender = ["Task1", "Task2", "Task3", "Task4", "Task5", "Task6"];
        const taskNames = { "Task1": "Problem Solving", "Task2": "Daily Life Conversations", "Task3": "Information", "Task4": "News Item", "Task5": "Discussion", "Task6": "Opinion" };

        return (
            <div className="listening-results-page">
                <div className="listening-celebration-header">
                    <h1 className="listening-celebration-title">🎉 Listening Test Completed! 🎉</h1>
                    <div className="listening-score-circle">
                        <div className="listening-score-ring">
                            <span className="listening-score-value">{score.correct}/{score.total}</span>
                            <span className="listening-score-label">{percentage}%</span>
                        </div>
                    </div>
                    <div className="listening-clb-display">
                        <span className="listening-clb-badge">{feedback.clb}</span>
                        <p className="listening-clb-feedback">{feedback.feedback}</p>
                    </div>
                </div>
                <div className="listening-task-results-grid">
                    {taskKeysRender.map((taskKey, index) => {
                        const taskAnswers = groupedByTask[taskKey];
                        if (!taskAnswers) return null;
                        const tScore = taskAnswers.filter(a => a.isCorrect).length;
                        return (
                            <div key={taskKey} className="listening-task-result-card" style={{ animationDelay: `${index * 0.1}s` }}>
                                <div className="listening-task-card-header">
                                    <span className="listening-task-number-badge">Part {index + 1}</span>
                                    <span className="listening-task-title">{taskNames[taskKey]}</span>
                                    <span className="listening-task-score-badge">{tScore}/{taskAnswers.length}</span>
                                </div>
                                <div className="listening-task-card-body">
                                    <div className="listening-answers-list">
                                        {taskAnswers.map((answer, subIndex) => {
                                            let qNum = answer.questionIndex + 1;
                                            if (answer.taskKey === "Task1.2") qNum += 2;
                                            if (answer.taskKey === "Task1.3") qNum += 5;
                                            return (
                                                <div key={subIndex} className={`listening-answer-item ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                                                    <span className="listening-q-label">Q{qNum}:</span>
                                                    <span className="listening-user-answer">{answer.selectedAnswer}</span>
                                                    {!answer.isCorrect && <span className="listening-correct-answer">✓ {answer.correctAnswer}</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="listening-feedback-actions" style={{ marginTop: 30 }}>
                    <button className="listening-action-btn listening-retry-btn" onClick={() => window.location.reload()}>🔄 Retake Test</button>
                    <button className="listening-action-btn listening-dashboard-btn" onClick={() => navigate('/dashboard')}>📊 Dashboard</button>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="paragon-exam-page-wrapper">
                {renderExamHeader()}
                {renderExamSubBar()}
                <div className="paragon-exam-body-content">
                    <p style={{ fontSize: 18, color: '#4a5568', marginTop: 50 }}>Loading test data...</p>
                </div>
                {renderExamBottomBar()}
                {renderExamFooter()}
            </div>
        );
    }

    if (error) {
        return (
            <div className="paragon-exam-page-wrapper">
                {renderExamHeader()}
                {renderExamSubBar()}
                <div className="paragon-exam-body-content">
                    <p style={{ fontSize: 18, color: '#c53030', marginTop: 50 }}>Error: {error}</p>
                </div>
                {renderExamBottomBar()}
                {renderExamFooter()}
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="paragon-exam-page-wrapper">
                {renderExamHeader()}
                <div className="paragon-exam-subbar">
                    <div className="paragon-exam-subbar-title">
                        Practice Test {testNumber === 1 ? 'A' : testNumber} - Listening Test Completed
                    </div>
                </div>
                <div className="paragon-exam-body-content">
                    <div className="listening-results-wrapper" style={{ width: '100%', maxWidth: 1000 }}>
                        {renderCompletion()}
                    </div>
                </div>
                {renderExamFooter()}
            </div>
        );
    }

    if (step === "submit") {
        handleSubmit();
        return (
            <div className="paragon-exam-page-wrapper">
                {renderExamHeader()}
                {renderExamSubBar()}
                <div className="paragon-exam-body-content">
                    <p style={{ fontSize: 18, color: '#4a5568', marginTop: 50 }}>Calculating score...</p>
                </div>
                {renderExamFooter()}
            </div>
        );
    }

    return (
        <div className="paragon-exam-page-wrapper">
            {renderExamHeader()}
            {renderExamSubBar()}

            <div className="paragon-exam-body-content">
                {showInstructions ? (
                    renderInstructionsPage()
                ) : showTaskIntro && taskIntroData[part] ? (
                    renderTaskIntroPage()
                ) : (
                    <div className="paragon-exam-test-card">
                        {renderActiveTestContent()}
                    </div>
                )}
            </div>

            {renderExamBottomBar()}
            {renderExamFooter()}
        </div>
    );
};

export default CelpipListeningAllParts;
